'use client'

import { useQuery } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { supabase } from '@/lib/supabase'

export interface StudyInsights {
  totalCards: number
  masteredCards: number // High stability (> 30 days)
  strugglingCards: number // Cards with 2+ lapses
  averageAccuracy: number // % of Good/Easy ratings
  totalReviews: number
  averageSessionTime: number // Average review duration in seconds
}

/**
 * Fetch study insights for the user across all their cards.
 * Provides high-level statistics about learning progress.
 */
export function useStudyInsights() {
  const { user } = useAuth()

  return useQuery<StudyInsights>({
    queryKey: ['study-insights', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return {
          totalCards: 0,
          masteredCards: 0,
          strugglingCards: 0,
          averageAccuracy: 0,
          totalReviews: 0,
          averageSessionTime: 0
        }
      }

      // Get card statistics
      const { data: cards } = await supabase
        .from('study_cards')
        .select('stability, lapses, reps')
        .eq('user_id', user.id)
        .gt('reps', 0) // Only cards that have been studied

      const totalCards = cards?.length ?? 0
      const masteredCards = cards?.filter((c) => c.stability >= 30).length ?? 0
      const strugglingCards = cards?.filter((c) => c.lapses >= 2).length ?? 0

      // Get recent review statistics (last 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString()
      const { data: reviews } = await supabase
        .from('study_review_logs')
        .select('rating, duration_ms')
        .eq('user_id', user.id)
        .gte('review_time', thirtyDaysAgo)

      const totalReviews = reviews?.length ?? 0
      const goodOrEasyReviews =
        reviews?.filter((r) => r.rating >= 3).length ?? 0
      const averageAccuracy =
        totalReviews > 0
          ? Math.round((goodOrEasyReviews / totalReviews) * 100)
          : 0

      const totalDuration =
        reviews?.reduce((sum, r) => sum + (r.duration_ms ?? 0), 0) ?? 0
      const averageSessionTime =
        totalReviews > 0 ? Math.round(totalDuration / totalReviews / 1000) : 0

      return {
        totalCards,
        masteredCards,
        strugglingCards,
        averageAccuracy,
        totalReviews,
        averageSessionTime
      }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5 // 5 minutes
  })
}
